import { Button } from "$components/button/button";
import { BinIcon } from "$components/icons";
import { TextField as RHFTextField } from "$components/rhf-fields";
import { useUser } from "$global-providers/user-provider";
import {
  Box,
  Collapse,
  IconButton,
  styled,
  Typography,
} from "@material-ui/core";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import {
  useNonPharmaLotsQuery,
  useNonPharmaSkuResolveQuery,
} from "./__generated__/non-pharma-dispense-queries.gql";
import {
  NonPharmaItemAutocomplete,
  type NonPharmaItemAutocompleteValue,
} from "./non-pharma-item-autocomplete";

const Row = styled(Box)(({ theme }) => ({
  display: "grid",
  // Below `sm`, stack so the autocomplete gets a full row and the
  // Qty + delete sit on a second row — at 390px the side-by-side grid
  // squeezed the autocomplete and clipped the selected item title under
  // the floating label.
  gridTemplateColumns: "1fr 100px auto",
  gap: theme.spacing(2),
  alignItems: "center",
  paddingBlock: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr auto",
    gridTemplateAreas: '"search search" "qty delete"',
    "& > :nth-child(1)": { gridArea: "search" },
    "& > :nth-child(2)": { gridArea: "qty" },
    "& > :nth-child(3)": { gridArea: "delete" },
  },
}));

const LotPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(1),
}));

const sortLotsFefo = <T extends { expiryDate: string; quantity: number }>(
  lots: ReadonlyArray<T>,
): ReadonlyArray<T> =>
  [...lots]
    .filter((l) => l.quantity > 0)
    .sort((a, b) => a.expiryDate.localeCompare(b.expiryDate));

type Props = {
  fieldName: string;
  // `null` means "no location pin"; passed to the GraphQL queries as
  // `undefined` so the backend doesn't treat "" as a real (and missing)
  // location id.
  locationId: string | null;
  onDelete: () => void;
};

export const NonPharmaItemRow: FC<Props> = ({
  fieldName,
  locationId,
  onDelete,
}) => {
  const { setValue, control } = useFormContext();
  const { facilityId } = useUser();
  const { formatMessage } = useIntl();

  const skuId = useWatch({ control, name: `${fieldName}.skuId` }) as string;
  const skuName = useWatch({
    control,
    name: `${fieldName}.skuName`,
  }) as string;
  const skuBrand = useWatch({
    control,
    name: `${fieldName}.skuBrand`,
  }) as string;
  const nonPharmaItemId = useWatch({
    control,
    name: `${fieldName}.nonPharmaItemId`,
  }) as string;
  const lotId = useWatch({
    control,
    name: `${fieldName}.lotId`,
  }) as string | null;

  // Coerce "" → undefined so the GraphQL variable is sent as null, not
  // an empty string. The backend treats "" as a real (but missing)
  // location id, which causes lookups to silently return no SKU.
  const locationIdVar = locationId || undefined;

  // Resolve the picked NonPharmaItem to a facility-specific SKU. The
  // catalogue lives in RxDB and works offline; the SKU mapping is
  // facility-/location-specific and lives server-side.
  const { data: skuData, loading: resolvingSku } = useNonPharmaSkuResolveQuery({
    variables: { facilityId, locationId: locationIdVar, nonPharmaItemId },
    skip: !nonPharmaItemId || !facilityId,
    fetchPolicy: "cache-and-network",
  });
  const resolvedSkuId = skuData?.inventorySearchTmp.edges[0]?.node.sku.id ?? "";

  // Depend only on the derived string so repeat Apollo cache emissions
  // don't clobber a user-picked lot override.
  useEffect(() => {
    if (resolvedSkuId !== skuId) {
      setValue(`${fieldName}.skuId`, resolvedSkuId);
      setValue(`${fieldName}.lotId`, null);
    }
  }, [resolvedSkuId, skuId, fieldName, setValue]);

  const { data, loading: loadingLots } = useNonPharmaLotsQuery({
    variables: { skuId, facilityId, locationId: locationIdVar },
    skip: !skuId,
    fetchPolicy: "cache-and-network",
  });

  const fefoLots = useMemo(
    () => sortLotsFefo(data?.getLotsBySkuId ?? []),
    [data?.getLotsBySkuId],
  );

  const fefoLot = fefoLots[0] ?? null;
  const isOverridden = !!lotId;

  useEffect(() => {
    if (lotId && fefoLots.length > 0 && !fefoLots.some((l) => l.id === lotId)) {
      setValue(`${fieldName}.lotId`, null);
    }
  }, [lotId, fefoLots, fieldName, setValue]);

  const itemPicked = !!nonPharmaItemId;
  const stockResolved = !!skuId;

  return (
    <Box>
      <Row>
        <NonPharmaItemAutocomplete
          value={
            nonPharmaItemId
              ? ({
                  id: nonPharmaItemId,
                  title: skuName,
                  brand: skuBrand || undefined,
                } satisfies NonPharmaItemAutocompleteValue)
              : null
          }
          onChange={(item) => {
            setValue(`${fieldName}.nonPharmaItemId`, item?.id ?? "");
            setValue(`${fieldName}.skuName`, item?.title ?? "");
            setValue(`${fieldName}.skuBrand`, item?.brand ?? "");
            setValue(`${fieldName}.skuId`, "");
            setValue(`${fieldName}.lotId`, null);
          }}
        />
        <RHFTextField
          name={`${fieldName}.quantity`}
          type="number"
          label={formatMessage({
            id: "nonPharmaDispense.quantity.label",
            defaultMessage: "Qty",
          })}
          inputProps={{ min: 1 }}
        />
        <IconButton onClick={onDelete} aria-label="Remove item">
          <BinIcon />
        </IconButton>
      </Row>

      <Collapse in={itemPicked}>
        <LotPanel>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">
              {resolvingSku ? (
                <FormattedMessage
                  id="nonPharmaDispense.sku.resolving"
                  defaultMessage="Checking stock at this location…"
                />
              ) : !stockResolved ? (
                <FormattedMessage
                  id="nonPharmaDispense.sku.notStocked"
                  defaultMessage="{name} is not stocked at this location"
                  values={{ name: skuName }}
                />
              ) : isOverridden ? (
                <FormattedMessage
                  id="nonPharmaDispense.lot.overrideActive"
                  defaultMessage="Dispensing from selected lot"
                />
              ) : fefoLot ? (
                <FormattedMessage
                  id="nonPharmaDispense.lot.fefoDefault"
                  defaultMessage="Default: nearest expiry ({date})"
                  values={{ date: fefoLot.expiryDate }}
                />
              ) : loadingLots ? (
                <FormattedMessage
                  id="nonPharmaDispense.lot.loading"
                  defaultMessage="Loading lots…"
                />
              ) : (
                <FormattedMessage
                  id="nonPharmaDispense.lot.noStock"
                  defaultMessage="No lots in stock at this location for {name}"
                  values={{ name: skuName }}
                />
              )}
            </Typography>
            {isOverridden && (
              <Button
                variant="text"
                color="primary"
                onClick={() => setValue(`${fieldName}.lotId`, null)}
              >
                <FormattedMessage
                  id="nonPharmaDispense.lot.clearOverride"
                  defaultMessage="Use default (FEFO)"
                />
              </Button>
            )}
          </Box>

          {stockResolved && fefoLots.length > 1 && !isOverridden && (
            <Box mt={1}>
              <Button
                variant="text"
                color="primary"
                onClick={() => setValue(`${fieldName}.lotId`, fefoLots[0].id)}
              >
                <FormattedMessage
                  id="nonPharmaDispense.lot.overrideToggle"
                  defaultMessage="Choose a specific lot"
                />
              </Button>
            </Box>
          )}

          {isOverridden && (
            <Box mt={1} display="flex" flexDirection="column" gridGap={4}>
              {fefoLots.map((lot) => (
                <Box
                  key={lot.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={lot.id === lotId}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  px={1}
                  py={0.5}
                  style={{
                    background: lot.id === lotId ? "#eef" : "transparent",
                    cursor: "pointer",
                  }}
                  onClick={() => setValue(`${fieldName}.lotId`, lot.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setValue(`${fieldName}.lotId`, lot.id);
                    }
                  }}
                >
                  <Typography variant="body2">
                    <FormattedMessage
                      id="nonPharmaDispense.lot.row"
                      defaultMessage="Expires {date} • {quantity} in stock"
                      values={{
                        date: lot.expiryDate,
                        quantity: lot.quantity,
                      }}
                    />
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </LotPanel>
      </Collapse>
    </Box>
  );
};

export { sortLotsFefo };
